import { isSha256Hex, sha256Hex } from './sha256.js';
/**
 * Battleship engine (WS-17c). Pure, synchronous, exhaustively unit tested — and the first
 * game kind in this module with **hidden information**, which is the whole reason it exists:
 * WS-15's sharing enforcement was built for exactly this shape and had, until now, nothing
 * that genuinely needed it.
 *
 * ## Where the secrets live
 *
 * A player's ship placement is written to a **per-user doc**, `game:<id>:user:<userId>`
 * (`gameUserDocName`). That name is hard-gated by the sync authorizer: only `<userId>` can
 * open it, enforced server-side at the WebSocket upgrade with a 403, needing no `doc_access`
 * row and un-widenable by one (see `../sharing/index.ts` — the rule is a name matcher, the
 * same mechanism `calendar:user:` and `lists:user:` already use). An opponent with devtools
 * and a hand-rolled socket gets a 403, not a fleet.
 *
 * Everything else — shots, results, sunk announcements, the final reveal — lives in the
 * **shared** `game:<id>` move log, family-visible, because spectating is the point.
 *
 * ## Commit and reveal: recorded now, checked if we ever need to
 *
 * The per-user doc keeps a placement *secret*, but secrecy alone doesn't stop the obvious
 * cheat: nothing physically prevents a player from editing their own placement doc mid-game
 * so an incoming shot "misses". So at ready-up, the client hashes its placement:
 *
 *     commit = sha256(canonicalPlacementJson(placement) + '|' + salt)
 *
 * and writes that 64-hex `commit` into the **shared** doc as a `commit` move. The salt is a
 * random 32-hex string kept in the private doc; without it the commit is not brute-forceable
 * from the (tiny, ~10^18 but structured) placement space alone. At game end — win, or
 * forfeit — each client writes a `reveal` move carrying its placement *and* salt into the
 * shared doc, which is also what makes the end screen able to show both fleets.
 *
 * **The engine deliberately does not check any of this** (owner call, 2026-08-04). Together
 * those two fields are enough to detect an after-the-fact cheat — re-hash the reveal, compare
 * to the commit — but building the check, its UI, and its four honest answers is work that
 * only pays off the day somebody actually cheats, and this is a family hub. So the commit
 * hash and the salted reveal go into the log now, while they cost two fields and a
 * `placementCommitHash` call, and sit there unexamined. If cheating ever happens, the
 * evidence is already recorded for every game ever played, and the verifier is a pure
 * function over data that already exists — that's the part that would have been expensive to
 * retrofit, and it isn't.
 *
 * ## Asynchronous resolution: "live, but not realtime"
 *
 * Only the defender can read their own placement, so only the defender's client can say
 * whether a shot hit. A shot therefore lands in the log unanswered, and the game sits in
 * `awaitingResolution` until any session of the defender's — phone, laptop, a tab left open
 * — sees the pending shot, resolves it against the private placement, and appends a
 * `resolve` move. The shooter sees "shot fired — waiting for the seas to answer" until then.
 *
 * That waiting is deliberately **silent**. There is no "you have a shot to resolve" push:
 * that would be a nag, and nags are forbidden (`memory/no-demand-mechanics.md`). Resolution
 * happens automatically whenever the defender has the app open, and the your-turn push
 * simply doesn't fire until the resolution lands. If the defender never opens the app, the
 * game just waits, indefinitely, saying nothing. See `apps/server/src/modules/games/notify.ts`.
 *
 * ## Rules chosen (and the ones deliberately not)
 *
 *  - 10x10 grid, classic fleet 5/4/3/3/2.
 *  - **Touching is allowed.** Ships may sit flush against each other. Real paper Battleship
 *    is split on this; the no-touching variant mostly generates "why can't I put it there"
 *    confusion on a phone-sized grid, so placement validation checks bounds and overlap only.
 *  - **Strict alternation.** The classic "hit = shoot again" variant is *not* in v1: with
 *    asynchronous resolution it would mean a player's extra shot is gated on a round-trip
 *    through the opponent's device, which reads as the game being broken rather than as a
 *    reward. Alternate-strict keeps "whose turn is it" answerable from the log alone.
 *  - Win = every one of the opponent's five ships sunk. Forfeit hands the win to the other
 *    player immediately, and still expects a reveal.
 *
 * ## Derive-time validation
 *
 * Same contract as `connect4.ts`, for the same CRDT reason: the move log is append-only and
 * Yjs's merged order — not wall-clock time — decides what "first" means. `deriveBattleship`
 * re-validates every move against the state it has already accepted and silently drops the
 * ones that don't hold up (out of turn, a cell already fired at, a resolution written by the
 * wrong player or for a shot that isn't pending, a second commit, a reveal before the game
 * ended, a "sunk" claimed with too few hits to have sunk it). Replay order wins; a dropped
 * move simply never happened, on every client identically.
 */
export const BATTLESHIP_SIZE = 10;
export const BATTLESHIP_SHIP_IDS = [
    'carrier',
    'battleship',
    'cruiser',
    'submarine',
    'destroyer',
];
/** The classic fleet: 5/4/3/3/2. */
export const BATTLESHIP_FLEET = [
    { id: 'carrier', name: 'Carrier', length: 5 },
    { id: 'battleship', name: 'Battleship', length: 4 },
    { id: 'cruiser', name: 'Cruiser', length: 3 },
    { id: 'submarine', name: 'Submarine', length: 3 },
    { id: 'destroyer', name: 'Destroyer', length: 2 },
];
/** Total occupied cells — 17. Sinking all of them is the win condition. */
export const BATTLESHIP_FLEET_CELLS = BATTLESHIP_FLEET.reduce((sum, s) => sum + s.length, 0);
export function battleshipShip(id) {
    return BATTLESHIP_FLEET.find((s) => s.id === id);
}
/* -------------------------------------------------------------------------- */
/* Cells and placement geometry                                                */
/* -------------------------------------------------------------------------- */
export function cellKey(cell) {
    return `${cell.row},${cell.col}`;
}
export function cellInBounds(cell) {
    return (Number.isInteger(cell.row) &&
        Number.isInteger(cell.col) &&
        cell.row >= 0 &&
        cell.row < BATTLESHIP_SIZE &&
        cell.col >= 0 &&
        cell.col < BATTLESHIP_SIZE);
}
/** The cells one ship occupies, bow-first. Empty when the ship id is unknown. */
export function shipCells(placement) {
    const ship = battleshipShip(placement.shipId);
    if (!ship)
        return [];
    const cells = [];
    for (let i = 0; i < ship.length; i += 1) {
        cells.push({
            row: placement.row + (placement.orientation === 'v' ? i : 0),
            col: placement.col + (placement.orientation === 'h' ? i : 0),
        });
    }
    return cells;
}
/**
 * Every ship exactly once, every cell on the board, no two ships sharing a cell. Ships
 * *touching* is fine on purpose — see this file's docblock.
 */
export function validatePlacement(placement) {
    const ships = placement?.ships;
    if (!Array.isArray(ships) || ships.length !== BATTLESHIP_FLEET.length) {
        return { ok: false, error: 'wrong-fleet' };
    }
    const seen = new Set();
    for (const ship of ships) {
        if (!ship || !battleshipShip(ship.shipId) || seen.has(ship.shipId)) {
            return { ok: false, error: 'wrong-fleet' };
        }
        seen.add(ship.shipId);
    }
    const occupied = new Set();
    for (const ship of ships) {
        if (ship.orientation !== 'h' && ship.orientation !== 'v') {
            return { ok: false, error: 'bad-orientation' };
        }
        const cells = shipCells(ship);
        for (const cell of cells) {
            if (!cellInBounds(cell))
                return { ok: false, error: 'out-of-bounds' };
            const key = cellKey(cell);
            if (occupied.has(key))
                return { ok: false, error: 'overlap' };
            occupied.add(key);
        }
    }
    return { ok: true };
}
/** Would placing (or moving) `candidate` alongside `others` be legal? The placement UI's
 * per-drop check — same rules as `validatePlacement`, scoped to one ship. */
export function canPlaceShip(candidate, others) {
    const cells = shipCells(candidate);
    if (cells.length === 0)
        return false;
    if (!cells.every(cellInBounds))
        return false;
    const taken = new Set(others.filter((o) => o.shipId !== candidate.shipId).flatMap((o) => shipCells(o).map(cellKey)));
    return cells.every((cell) => !taken.has(cellKey(cell)));
}
/** Which ship (if any) sits on `cell`. */
export function shipAt(placement, cell) {
    const key = cellKey(cell);
    for (const ship of placement.ships) {
        if (shipCells(ship).some((c) => cellKey(c) === key))
            return ship.shipId;
    }
    return null;
}
/**
 * "Most people just shuffle" — the random-placement button, and the fallback if someone
 * ready-ups without touching the grid. `rand` is injectable so tests are deterministic.
 */
export function randomPlacement(rand = Math.random) {
    // Largest ship first: placing the Carrier last into a crowded board is what makes naive
    // random placement retry forever.
    const ordered = [...BATTLESHIP_FLEET].sort((a, b) => b.length - a.length);
    for (let attempt = 0; attempt < 200; attempt += 1) {
        const ships = [];
        let failed = false;
        for (const ship of ordered) {
            let placed = false;
            for (let tries = 0; tries < 400 && !placed; tries += 1) {
                const orientation = rand() < 0.5 ? 'h' : 'v';
                const span = ship.length - 1;
                const row = Math.floor(rand() * (BATTLESHIP_SIZE - (orientation === 'v' ? span : 0)));
                const col = Math.floor(rand() * (BATTLESHIP_SIZE - (orientation === 'h' ? span : 0)));
                const candidate = { shipId: ship.id, row, col, orientation };
                if (canPlaceShip(candidate, ships)) {
                    ships.push(candidate);
                    placed = true;
                }
            }
            if (!placed) {
                failed = true;
                break;
            }
        }
        if (!failed)
            return { ships: sortPlacementShips(ships) };
    }
    // Unreachable with a sane `rand` (a 10x10 board fits this fleet trivially), but a
    // guaranteed-legal fallback beats throwing at the one moment someone wants to play.
    return {
        ships: sortPlacementShips(BATTLESHIP_FLEET.map((ship, i) => ({
            shipId: ship.id,
            row: i * 2,
            col: 0,
            orientation: 'h',
        }))),
    };
}
/** 32 hex chars of salt — enough that a commit can't be brute-forced back to a placement. */
export function randomSalt(rand = Math.random) {
    let out = '';
    for (let i = 0; i < 32; i += 1)
        out += Math.floor(rand() * 16).toString(16);
    return out;
}
/* -------------------------------------------------------------------------- */
/* Commit–reveal                                                               */
/* -------------------------------------------------------------------------- */
/**
 * The exact bytes that get hashed. Deterministic by construction: ships sorted by their
 * order in `BATTLESHIP_FLEET`, fields written in a fixed order, no whitespace — so two
 * clients that agree on the placement always agree on the hash, whatever order the ships
 * happen to sit in inside the Yjs doc.
 */
export function canonicalPlacementJson(placement) {
    const ships = sortPlacementShips(placement.ships).map((s) => `{"shipId":"${s.shipId}","row":${s.row},"col":${s.col},"orientation":"${s.orientation}"}`);
    return `{"ships":[${ships.join(',')}]}`;
}
function sortPlacementShips(ships) {
    const order = (id) => BATTLESHIP_SHIP_IDS.indexOf(id);
    return [...ships].sort((a, b) => order(a.shipId) - order(b.shipId));
}
/** `sha256(canonicalPlacementJson + '|' + salt)` — the value written into the shared doc. */
export function placementCommitHash(placement, salt) {
    return sha256Hex(`${canonicalPlacementJson(placement)}|${salt}`);
}
function emptyState() {
    return {
        phase: 'placing',
        commits: [null, null],
        shots: [],
        shotsByShooter: [[], []],
        turn: null,
        awaitingResolution: null,
        sunkByShooter: [[], []],
        hitsByShooter: [0, 0],
        winner: null,
        winReason: null,
        reveals: [null, null],
        rejectedMoveIndexes: [],
        appliedMoves: 0,
    };
}
function other(index) {
    return index === 0 ? 1 : 0;
}
/**
 * Replays `moves` in merged-array order, validating each against the state accepted so far,
 * and returns everything a board, a spectator view, a replay frame, or the push notifier
 * needs. The only authority on whose turn it is and whether the game is over.
 *
 * Never throws on malformed input: an unrecognised or structurally broken move is simply
 * rejected, because a move log is untrusted data that arrived over a CRDT.
 */
export function deriveBattleship(moves, playerIds) {
    const state = emptyState();
    const indexOf = (playerId) => {
        if (playerId === playerIds[0])
            return 0;
        if (playerId === playerIds[1])
            return 1;
        return null;
    };
    const firedCells = [new Set(), new Set()];
    let nextShooter = 0;
    let pending = null;
    const reject = (i) => {
        state.rejectedMoveIndexes.push(i);
    };
    for (let i = 0; i < moves.length; i += 1) {
        const move = moves[i];
        if (!move || typeof move !== 'object') {
            reject(i);
            continue;
        }
        const playerIndex = indexOf(move.playerId);
        if (playerIndex === null) {
            reject(i);
            continue;
        }
        const bothCommitted = state.commits[0] !== null && state.commits[1] !== null;
        switch (move.type) {
            case 'commit': {
                // One commit per player, and only before the shooting starts — a second commit is
                // exactly the "I changed my mind about where my ships are" move this design exists
                // to make impossible, so it is dropped rather than merged.
                if (state.commits[playerIndex] !== null ||
                    !isSha256Hex(move.hash) ||
                    state.winner !== null) {
                    reject(i);
                    continue;
                }
                state.commits[playerIndex] = move.hash;
                break;
            }
            case 'shot': {
                if (!bothCommitted || state.winner !== null || pending !== null) {
                    reject(i);
                    continue;
                }
                if (playerIndex !== nextShooter || !move.cell || !cellInBounds(move.cell)) {
                    reject(i);
                    continue;
                }
                const key = cellKey(move.cell);
                if (firedCells[playerIndex].has(key)) {
                    reject(i); // firing at the same water twice is a duplicate, not a wasted turn
                    continue;
                }
                firedCells[playerIndex].add(key);
                const shot = {
                    index: state.shots.length,
                    moveIndex: i,
                    shooterIndex: playerIndex,
                    cell: { row: move.cell.row, col: move.cell.col },
                    result: null,
                };
                state.shots.push(shot);
                state.shotsByShooter[playerIndex].push(shot);
                pending = shot;
                break;
            }
            case 'resolve': {
                if (!pending || move.shotIndex !== pending.index) {
                    reject(i);
                    continue;
                }
                const defenderIndex = other(pending.shooterIndex);
                // Only the defender can answer — they're the only one who can read the placement.
                if (playerIndex !== defenderIndex) {
                    reject(i);
                    continue;
                }
                if (move.result !== 'hit' && move.result !== 'miss') {
                    reject(i);
                    continue;
                }
                const shooter = pending.shooterIndex;
                const sunkShipId = move.sunkShipId;
                if (sunkShipId !== undefined) {
                    const ship = battleshipShip(sunkShipId);
                    if (move.result !== 'hit' || !ship || state.sunkByShooter[shooter].includes(sunkShipId)) {
                        reject(i);
                        continue;
                    }
                    // You cannot have sunk more ship-cells than you have landed hits.
                    const cellsClaimed = state.sunkByShooter[shooter].reduce((sum, id) => sum + (battleshipShip(id)?.length ?? 0), 0) + ship.length;
                    if (state.hitsByShooter[shooter] + 1 < cellsClaimed) {
                        reject(i);
                        continue;
                    }
                    state.sunkByShooter[shooter].push(sunkShipId);
                    pending.sunkShipId = sunkShipId;
                }
                pending.result = move.result;
                if (move.result === 'hit')
                    state.hitsByShooter[shooter] += 1;
                pending = null;
                if (state.sunkByShooter[shooter].length === BATTLESHIP_FLEET.length) {
                    state.winner = shooter;
                    state.winReason = 'fleet-sunk';
                }
                else {
                    nextShooter = other(shooter);
                }
                break;
            }
            case 'reveal': {
                // Revealing early would hand the opponent the board, so the engine refuses to see it
                // as a reveal at all — the log may contain the bytes, but replay never applies them.
                if (state.winner === null || state.reveals[playerIndex] !== null) {
                    reject(i);
                    continue;
                }
                if (typeof move.salt !== 'string' || !move.salt || !move.placement) {
                    reject(i);
                    continue;
                }
                state.reveals[playerIndex] = { placement: move.placement, salt: move.salt };
                break;
            }
            case 'forfeit': {
                if (state.winner !== null) {
                    reject(i);
                    continue;
                }
                state.winner = other(playerIndex);
                state.winReason = 'forfeit';
                pending = null;
                break;
            }
            default:
                reject(i);
                continue;
        }
        state.appliedMoves += 1;
    }
    const bothCommitted = state.commits[0] !== null && state.commits[1] !== null;
    state.phase = state.winner !== null ? 'done' : bothCommitted ? 'battle' : 'placing';
    state.awaitingResolution = pending
        ? {
            shotIndex: pending.index,
            shooterIndex: pending.shooterIndex,
            defenderIndex: other(pending.shooterIndex),
            cell: pending.cell,
        }
        : null;
    state.turn = state.phase === 'battle' && pending === null ? nextShooter : null;
    return state;
}
/**
 * What the **defender's** client computes before writing a `resolve` move: given their own
 * (private) placement, the incoming cell, and the cells already hit on them, is it a hit,
 * and did it finish a ship off? Also the engine's own cross-check at reveal time.
 */
export function resolveShotAgainst(placement, cell, previousHitCells) {
    const hitShipId = shipAt(placement, cell);
    if (!hitShipId)
        return { result: 'miss' };
    const ship = placement.ships.find((s) => s.shipId === hitShipId);
    if (!ship)
        return { result: 'miss' };
    const sunk = shipCells(ship).every((c) => cellKey(c) === cellKey(cell) || previousHitCells.has(cellKey(c)));
    return sunk ? { result: 'hit', sunkShipId: hitShipId } : { result: 'hit' };
}
/** The cells a shooter has confirmed hits on — what `resolveShotAgainst` needs as history,
 * and what the defender's own board overlays. */
export function hitCellsAgainst(state, defenderIndex) {
    const hits = new Set();
    for (const shot of state.shotsByShooter[other(defenderIndex)]) {
        if (shot.result === 'hit')
            hits.add(cellKey(shot.cell));
    }
    return hits;
}
/** Ships a player still has afloat. */
export function remainingShips(state, playerIndex) {
    const sunk = new Set(state.sunkByShooter[other(playerIndex)]);
    return BATTLESHIP_FLEET.filter((ship) => !sunk.has(ship.id));
}
/** "You sank their Cruiser" / "They sank your Cruiser" — a quiet fact, never a fanfare. */
export function sunkAnnouncement(shipId, byMe) {
    const name = battleshipShip(shipId)?.name ?? 'ship';
    return byMe ? `You sank their ${name}` : `They sank your ${name}`;
}
/** "B7" — the human name for a cell, columns A-J across, rows 1-10 down. */
export function cellLabel(cell) {
    return `${String.fromCharCode(65 + cell.col)}${cell.row + 1}`;
}
//# sourceMappingURL=battleship.js.map